import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts';
import { SOCPoint, BESSConfig } from '@/types/epse';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface EPSESOCChartProps {
  socTrajectory: SOCPoint[];
  bessConfig: BESSConfig;
  title?: string;
}

export function EPSESOCChart({ socTrajectory, bessConfig, title = 'SOC Verloop' }: EPSESOCChartProps) {
  // Sample data if too many points (for performance)
  const maxPoints = 500;
  const sampleRate = Math.ceil(socTrajectory.length / maxPoints);
  const sampledData = socTrajectory.filter((_, i) => i % sampleRate === 0);
  
  const chartData = sampledData.map(point => ({
    timestamp: point.timestamp,
    time: format(new Date(point.timestamp), 'dd/MM HH:mm', { locale: nl }),
    soc: point.soc_percent * 100,
    soc_kwh: point.soc_kwh,
  }));
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          State of Charge over tijd
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="time"
                tick={{ fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  if (name === 'soc') return [`${value.toFixed(1)}%`, 'SOC'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Tijd: ${label}`}
              />
              
              {/* SOC limits */}
              <ReferenceLine 
                y={bessConfig.soc_max * 100} 
                stroke="hsl(var(--primary))" 
                strokeDasharray="5 5"
                strokeOpacity={0.5}
              />
              <ReferenceLine 
                y={bessConfig.soc_min * 100} 
                stroke="hsl(var(--destructive))" 
                strokeDasharray="5 5"
                strokeOpacity={0.5}
              />
              
              <defs>
                <linearGradient id="socGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              
              <Area 
                type="monotone" 
                dataKey="soc" 
                stroke="hsl(142, 76%, 36%)" 
                fill="url(#socGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
